import React, { useState, useEffect, useMemo } from 'react';
import { ArrowRight, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { resources as mockResources } from '../data/mockData';
import ResourceCard from './ResourceCard';
import { useScrollReveal } from '../hooks/useScrollReveal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Resources = () => {
  const [ctaRef, isCtaVisible] = useScrollReveal();
  const [resourcesRef, isResourcesVisible] = useScrollReveal();
  const [resources, setResources] = useState(mockResources);
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    fetch(`${API}/posts`)
      .then(r => r.json())
      .then(data => { if (data?.length > 0) setResources(data); })
      .catch(() => {});
  }, []);

  const categories = useMemo(() => {
    const set = new Set(resources.map((r) => r.category).filter(Boolean));
    return ['All', ...Array.from(set)];
  }, [resources]);

  const filteredResources = useMemo(() => {
    const q = query.trim().toLowerCase();
    return resources.filter((r) => {
      const matchesCategory = activeCategory === 'All' || r.category === activeCategory;
      if (!matchesCategory) return false;
      if (!q) return true;
      return (
        (r.title || '').toLowerCase().includes(q) ||
        (r.excerpt || '').toLowerCase().includes(q) ||
        (r.category || '').toLowerCase().includes(q)
      );
    });
  }, [resources, query, activeCategory]);

  return (
    <>
      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-[#0a0b10] relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          {Array.from({ length: 15 }).map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full"
              style={{
                width: Math.random() * 4 + 2 + 'px',
                height: Math.random() * 4 + 2 + 'px',
                left: Math.random() * 100 + '%',
                top: Math.random() * 100 + '%',
                background: '#00BCE4',
                opacity: Math.random() * 0.3 + 0.1,
                animation: `float-particle ${Math.random() * 8 + 6}s ease-in-out infinite ${Math.random() * 5}s`,
              }}
            />
          ))}
        </div>

        {/* Celebrating mascot - left side */}
        <img
          src="/immi-yay-nobg.webp"
          alt="Immi mascot celebrating"
          className="absolute left-8 lg:left-16 bottom-4 w-40 lg:w-52 h-auto hidden md:block"
          style={{ animation: 'mascot-float 3.5s ease-in-out infinite', filter: 'drop-shadow(0 10px 40px rgba(0,188,228,0.5))', mixBlendMode: 'screen' }}
        />

        <div
          ref={ctaRef}
          className={`relative z-10 section-container text-center reveal ${isCtaVisible ? 'visible' : ''}`}
        >
          <span
            className="block text-sm font-semibold text-[#00BCE4] uppercase tracking-[3px] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Contact our friendly team to discuss your options or to book a demo
          </span>
          <h2
            className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight max-w-4xl mx-auto"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Begin your journey to stronger business foundations with Imprint
          </h2>
          <p
            className="text-base md:text-lg text-gray-400 mb-6 max-w-2xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Partner with Imprint the Integration and Print & Packaging MIS specialists.
            Providing solutions to maximise your efficiencies and strengthen your company for the long term.
          </p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="btn-primary text-xs"
          >
            Let's Talk
            <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* Resources Section */}
      <section id="resources" className="py-20 lg:py-28 bg-white">
        <div
          ref={resourcesRef}
          className={`section-container reveal ${isResourcesVisible ? 'visible' : ''}`}
        >
          {/* Header */}
          <div className="mb-14">
            <span
              className="block text-sm font-semibold text-[#00BCE4] uppercase tracking-[3px] mb-3"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Resources
            </span>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Useful and interesting<br />
              <span className="text-[#00BCE4]">information</span>
            </h2>
          </div>

          {/* Search + Category Filter */}
          <div className="mb-10 flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="search"
                data-testid="resources-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search resources..."
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-[#00BCE4] focus:ring-2 focus:ring-[#00BCE4]/20 transition-all duration-300 outline-none"
                style={{ fontFamily: 'Inter, sans-serif' }}
              />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="resources-category-filters">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  data-testid={`resources-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-all duration-300 ${
                    activeCategory === cat
                      ? 'bg-[#00BCE4] text-white shadow-md shadow-[#00BCE4]/30'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Resource Cards */}
          {filteredResources.length === 0 ? (
            <div data-testid="resources-empty-state" className="text-center py-16 text-gray-400">
              <p className="text-base" style={{ fontFamily: 'Inter, sans-serif' }}>
                No resources match your search. Try a different keyword or category.
              </p>
            </div>
          ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredResources.map((resource) => (
              <ResourceCard key={resource.slug} resource={resource} />
            ))}
          </div>
          )}

          {/* View all */}
          <div className="mt-12 text-center">
            <Link
              to="/resources"
              data-testid="view-all-resources-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#00BCE4] hover:gap-3 transition-all duration-300"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              View All Resources <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Resources;
